import React from 'react';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import { zoomPlugin } from '@react-pdf-viewer/zoom';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/zoom/lib/styles/index.css';
import './ResponsivePdfViewer.css';
import { Typography } from 'antd';
const { Text } = Typography;

const resolveDefaultWorkerUrl = () => {
   if (typeof window === 'undefined') {
      return `${process.env.PUBLIC_URL || ''}/js/pdf.worker.min.js`;
   }

   const publicUrl = process.env.PUBLIC_URL;
   const hasExplicitPublicUrl = publicUrl && publicUrl !== '.';

   if (hasExplicitPublicUrl) {
      const normalizedPublicUrl = publicUrl.endsWith('/') ? publicUrl : `${publicUrl}/`;
      return new URL('js/pdf.worker.min.js', `${window.location.origin}${normalizedPublicUrl}`).toString();
   }

   const scriptWithStaticPath = Array.from(document.getElementsByTagName('script'))
      .map((script) => script.src)
      .find((src) => src && src.includes('/static/js/'));

   if (scriptWithStaticPath) {
      const [appBaseUrl] = scriptWithStaticPath.split('/static/js/');
      if (appBaseUrl) {
         return `${appBaseUrl}/js/pdf.worker.min.js`;
      }
   }

   return `${window.location.origin}/js/pdf.worker.min.js`;
};

const DEFAULT_WORKER_URL = resolveDefaultWorkerUrl();
const EMPTY_PAGE_LAYOUT = Object.freeze({});

const getFunctionSignature = (fn) => (typeof fn === 'function' ? fn.toString() : '');

const arePageLayoutsEqual = (previousLayout, nextLayout) => {
   if (previousLayout === nextLayout) {
      return true;
   }

   if (!previousLayout || !nextLayout) {
      return !previousLayout && !nextLayout;
   }

   return (
      getFunctionSignature(previousLayout.transformSize) === getFunctionSignature(nextLayout.transformSize) &&
      getFunctionSignature(previousLayout.buildPageStyles) === getFunctionSignature(nextLayout.buildPageStyles)
   );
};

const ResponsivePdfViewer = ({
   fileUrl,
   height = '100%',
   workerUrl = DEFAULT_WORKER_URL,
   showControls = true,
   pageLayout,
   secureMode = false,
   watermarkText = '',
   renderError,
   onDocumentLoad,
   renderToolbarExtra,
}) => {
   const zoomPluginInstance = zoomPlugin();
   const { CurrentScale, ZoomIn, ZoomOut } = zoomPluginInstance;
   const normalizedPageLayout = pageLayout || EMPTY_PAGE_LAYOUT;

   const stopEventPropagation = (event) => {
      event.stopPropagation();
   };

   const fitToWidth = () => {
      if (typeof requestAnimationFrame === 'function') {
         requestAnimationFrame(() => {
            if (zoomPluginInstance.zoomTo) {
               zoomPluginInstance.zoomTo(SpecialZoomLevel.PageWidth);
            }
         });
         return;
      }

      if (zoomPluginInstance.zoomTo) {
         zoomPluginInstance.zoomTo(SpecialZoomLevel.PageWidth);
      }
   };

   const handleDocumentLoad = (event) => {
      fitToWidth();
      if (typeof onDocumentLoad === 'function') {
         onDocumentLoad(event);
      }
   };

   const handleSecureContextMenu = (event) => {
      if (secureMode) {
         event.preventDefault();
      }
   };

   const handleSecureDragStart = (event) => {
      if (secureMode) {
         event.preventDefault();
      }
   };

   return (
      <div
         className={`responsive-pdf-viewer ${secureMode ? 'responsive-pdf-viewer--secure' : ''}`}
         style={{ height }}
         onContextMenu={handleSecureContextMenu}
         onDragStart={handleSecureDragStart}
      >
         <div className="responsive-pdf-viewer__content">
            <Worker workerUrl={workerUrl}>
               <Viewer
                  key={fileUrl}
                  fileUrl={fileUrl}
                  plugins={[zoomPluginInstance]}
                  defaultScale={SpecialZoomLevel.PageWidth}
                  onDocumentLoad={handleDocumentLoad}
                  initialPage={0}
                  pageLayout={normalizedPageLayout}
                  renderError={renderError}
               />
            </Worker>
         </div>

         {secureMode && watermarkText && (
            <div className="responsive-pdf-viewer__watermark-layer" aria-hidden="true">
               <span className="responsive-pdf-viewer__watermark">{watermarkText}</span>
               <span className="responsive-pdf-viewer__watermark responsive-pdf-viewer__watermark--2">{watermarkText}</span>
               <span className="responsive-pdf-viewer__watermark responsive-pdf-viewer__watermark--3">{watermarkText}</span>
               <span className="responsive-pdf-viewer__watermark responsive-pdf-viewer__watermark--4">{watermarkText}</span>
            </div>
         )}

         {showControls && (
            <div
               className="responsive-pdf-viewer__toolbar"
               style={{ justifyContent: 'center' }}
               onClick={stopEventPropagation}
               onPointerDown={stopEventPropagation}
               onTouchStart={stopEventPropagation}
            >
               <ZoomOut>
                  {(props) => (
                     <button
                        type="button"
                        className="responsive-pdf-viewer__btn"
                        onClick={(event) => {
                           event.stopPropagation();
                           props.onClick(event);
                        }}
                        onPointerDown={stopEventPropagation}
                        aria-label="Zoom out"
                     >
                        -
                     </button>
                  )}
               </ZoomOut>

               <CurrentScale>
                  {(props) => (
                     <span className="responsive-pdf-viewer__scale">{Math.round(props.scale * 100)}%</span>
                  )}
               </CurrentScale>

               <ZoomIn>
                  {(props) => (
                     <button
                        type="button"
                        className="responsive-pdf-viewer__btn"
                        onClick={(event) => {
                           event.stopPropagation();
                           props.onClick(event);
                        }}
                        onPointerDown={stopEventPropagation}
                        aria-label="Zoom in"
                     >
                        +
                     </button>
                  )}
               </ZoomIn>

               <button
                  type="button"
                  className="responsive-pdf-viewer__btn responsive-pdf-viewer__btn--fit"
                  onClick={(event) => {
                     event.stopPropagation();
                     fitToWidth();
                  }}
                  onPointerDown={stopEventPropagation}
               >
                  <Text ellipsis>Fit to width</Text>
               </button>

               {typeof renderToolbarExtra === 'function' && (
                  <div className="responsive-pdf-viewer__toolbar-extra">
                     {renderToolbarExtra()}
                  </div>
               )}
            </div>
         )}
      </div>
   );
};

const areResponsivePdfViewerPropsEqual = (previousProps, nextProps) => (
   previousProps.fileUrl === nextProps.fileUrl &&
   previousProps.height === nextProps.height &&
   previousProps.workerUrl === nextProps.workerUrl &&
   previousProps.showControls === nextProps.showControls &&
   previousProps.secureMode === nextProps.secureMode &&
   previousProps.watermarkText === nextProps.watermarkText &&
   previousProps.renderError === nextProps.renderError &&
   previousProps.renderToolbarExtra === nextProps.renderToolbarExtra &&
   arePageLayoutsEqual(previousProps.pageLayout, nextProps.pageLayout)
);

export default React.memo(ResponsivePdfViewer, areResponsivePdfViewerPropsEqual);
